import type { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { AppConstants } from '../app-constants';

/**
 * localStorage.clear() for a dead / forced-out session that STILL keeps what must outlive it:
 *  - the product-tour "seen" flags (`tms_tour_*`);
 *  - the "last user on this device" markers (LAST_DEVICE_USER_EMAIL / LAST_DEVICE_TENANT_ID).
 *
 * The markers matter: the NEXT login compares against them (wipeOfflineDataIfDifferentUser) to notice a different
 * user or company and wipe the previous one's offline cache. Without them a different user is not recognised, so the
 * previous user's saved profile picture, menu and lists stay on the phone and show up for the new user.
 * (Found 2026-09-30: the login page's own `sessionExpired` clear and the forced-logout dialog both dropped them,
 * even though LogoutService and the 401 interceptor had carefully kept them just before.)
 */
export function clearLocalStorageKeepingDeviceIdentity(): void {
  const keepKeys = [AppConstants.LAST_DEVICE_USER_EMAIL, AppConstants.LAST_DEVICE_TENANT_ID];
  const kept: [string, string][] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k) continue;
    if (k.startsWith('tms_tour_') || keepKeys.includes(k)) {
      const v = localStorage.getItem(k);
      if (v !== null) kept.push([k, v]);
    }
  }
  localStorage.clear();
  kept.forEach(([k, v]) => localStorage.setItem(k, v));
}

/**
 * Safety net for the PHONE APP only (Android/iPhone): after the user's identity or rights changed on the server
 * (finished onboarding / signup), go to the target screen and then reload the app in place, so the header, sidebar
 * and every cached copy start again from what the server says now. Reloading the CURRENT page never assigns a new
 * URL, so iOS does not hand it off to Safari (see LoginComponent.navigateAfterLogin). The website is untouched.
 */
export function navigateThenReloadOnNative(router: Router, url: string): void {
  router.navigateByUrl(url, { replaceUrl: true }).then(() => {
    if (Capacitor.isNativePlatform()) {
      // A moment for the target screen's own redirect (if any) to settle, then a fresh start.
      setTimeout(() => window.location.reload(), 600);
    }
  });
}
